import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import {
  buildGeneralDataDefaultValues,
  buildProfileUpdatePayloadForGeneral,
  generalDataProfileSchema,
  GeneralFieldValues,
} from '@/pages/UserAccount/Profile';
import { FormHasDirtyFields } from '@/pages/UserAccount/Profile/ui/ProfilePresentational';

import { FormDatePicker, FormInputPhone, FormSelect, FormTextInput } from '@/shared/ui';

import { genders } from '@/components/Forms/constants';

import AuthContext from '@/contexts/Auth/AuthContext';

import api from '@/api';

import BaseChapterTemplate from './BaseChapterTemplate/BaseChapterTemplate';

const generalDataFields = [
  'firstName',
  'lastName',
  'middleName',
  'birthday',
  'gender',
  'contactEmail',
  'contactPhone',
] satisfies Array<keyof GeneralFieldValues>;

interface GeneralDataProps {
  setFormHasDirtyFields: (value: FormHasDirtyFields) => void;
  activeKey: Array<NonNullable<FormHasDirtyFields>>;
}

export const GeneralData: FC<GeneralDataProps> = ({ setFormHasDirtyFields, activeKey }) => {
  const authContext = useContext(AuthContext);
  const defaultValues = useMemo(() => buildGeneralDataDefaultValues(authContext), [authContext]);
  const {
    control,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<GeneralFieldValues>({
    resolver: zodResolver(generalDataProfileSchema) as any,
    reValidateMode: 'onChange',
    shouldFocusError: false,
    defaultValues,
  });

  const onSubmit = async (values: GeneralFieldValues): Promise<void> => {
    const newProfileData = buildProfileUpdatePayloadForGeneral(values);
    try {
      const res = await api.updateUserProfile(newProfileData);
      authContext.updateUserData(res);
    } catch (error) {
      console.error(error);
    }
  };

  const onReset = useCallback((): void => {
    reset(buildGeneralDataDefaultValues(authContext));
  }, [authContext]);

  useEffect(() => {
    onReset();
  }, [defaultValues, onReset, authContext]);

  const isGeneralDataFieldsDirty = Object.keys(dirtyFields).some((key) =>
    generalDataFields.includes(key as keyof GeneralFieldValues),
  );

  useEffect(() => {
    if (!isGeneralDataFieldsDirty) return setFormHasDirtyFields(null);

    setFormHasDirtyFields('general');
  }, [isGeneralDataFieldsDirty]);

  useEffect(() => {
    if (!activeKey.includes('general') && isGeneralDataFieldsDirty) onReset();
  }, [activeKey]);

  const availableGenders = genders.filter(({ id }) => id !== 'U');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <BaseChapterTemplate
        fields={[
          <FormTextInput control={control} name="lastName" label="Прізвище" key="lastName" disabled required />,
          <FormTextInput control={control} name="firstName" label="Ім'я" key="firstName" disabled required />,
          <FormTextInput
            control={control}
            name="middleName"
            label="По батькові"
            placeholder="Не вказано"
            key="middleName"
            disabled
          />,
          <FormDatePicker
            control={control}
            name="birthday"
            label="Дата народження"
            key="birthday"
            maxDateToday
            required
          />,
          <FormSelect
            options={availableGenders}
            control={control}
            placeholder="Виберіть варіант зі списку"
            name="gender"
            label="Стать"
            key="gender"
          />,
          <FormTextInput
            type="email"
            control={control}
            name="contactEmail"
            label="Email"
            key="contactEmail"
            placeholder="example@example.com"
            required
            inputMode="email"
          />,
          <FormInputPhone control={control} name="contactPhone" label="Номер телефону" key="contactPhone" required />,
        ]}
        isDisabledSubmitAction={isGeneralDataFieldsDirty}
        onReset={onReset}
      />
    </form>
  );
};
